import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {IAppState} from '../store/IAppState';
import {NgRedux, select} from '@angular-redux/store';
import {canHide, getPageFromUrl, Page} from '../shared/helpers';
import {Observable, Subscription} from 'rxjs';
import {NavigationEnd, Router} from '@angular/router';
import {ExportPdfDialogComponent} from '../export-pdf-dialog/export-pdf-dialog.component';
import {FeatureToggleSetting} from '../shared/models';
import {FeatureToggleEntries} from '../shared/feature-toggle-settings-enum';

@Component({
  selector: 'app-navigation-bar',
  templateUrl: './navigation-bar.component.html',
  styleUrls: ['./navigation-bar.component.scss'],
})
export class NavigationBarComponent implements OnInit, OnDestroy {
  page: Page;
  shouldPayersBeGranular = false;
  payerTabPermitted = false;
  lagKeyTabPermitted = false;
  billingAreaTabPermitted = false;
  invoiceStatusTabPermitted = false;
  visitTypeTabPermitted = false;
  scrollY = 0;
  private routerSubscription: Subscription;
  private featureSubscription: Subscription;
  private readonly pagesWithGranularPayers: Page[] = [
    Page.CollectionsSnapshot,
    Page.CollectionsByPayer,
    Page.CollectionsSummary,
    Page.CollectionsDepartment,
    Page.CollectionsSpecialty,
    Page.CollectionsProvider,
    Page.DenialsDepartment,
    Page.DenialsSpecialty,
    Page.DenialsProvider,
    Page.DenialsByPayer
  ];

  @ViewChild('pdfContent', {static: true}) pdfContent: ExportPdfDialogComponent;

  @select(['display', 'filterBannerEdit'])
  readonly filterBannerEdit$: Observable<boolean>;
  @select(['data', 'featureToggleSettings'])
  private readonly featureToggleSettings$: Observable<FeatureToggleSetting[]>;
  canHideUpdatedFilter = true;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }

  constructor(
    private ngRedux: NgRedux<IAppState>,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.setFilterTabsAvailabilities(this.router.url);
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        this.setPage(url);
        this.setFilterTabsAvailabilities(url);
        this.setPayerGranularity();
      }
    });
    this.featureSubscription = this.featureToggleSettings$.subscribe(
      (settings: FeatureToggleSetting[]) => {
      this.canHideUpdatedFilter = canHide(FeatureToggleEntries.UPDATED_FILTER_BANNER, settings,
        this.ngRedux.getState().data.userProfile);
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.featureSubscription?.unsubscribe();
  }

  private setPage(url: string) {
    this.page = getPageFromUrl(url);
  }

  private setPayerGranularity() {
    this.shouldPayersBeGranular = !!this.pagesWithGranularPayers.find(page => page === this.page);
  }

  private setFilterTabsAvailabilities(url: string) {
    this.setPayerTabAvailability(url);
    this.setLagTabAvailability(url);
    this.setBillingAreaTabAvailability(url);
    this.setInvoiceStatusTabAvailability(url);
    this.setVisitTypeTabAvailability(url);
  }

  private setVisitTypeTabAvailability(url: string) {
    this.visitTypeTabPermitted = url.includes('overview')
      || url.includes('npv') || url.includes('em');
  }

  private setInvoiceStatusTabAvailability(url: string) {
    this.invoiceStatusTabPermitted = url.includes('overview')
      || url.includes('collections');
  }

  private setBillingAreaTabAvailability(url: string) {
    this.billingAreaTabPermitted = url.includes('overview')
      || url.includes('collections')
      || url.includes('denials');
  }

  private setLagTabAvailability(url: string) {
    this.lagKeyTabPermitted = url.includes('overview')
      || url.includes('collections')
      || url.includes('denials');
  }

  private setPayerTabAvailability(url: string) {
    this.payerTabPermitted = url.includes('overview')
      || url.includes('npv')
      || url.includes('collections')
      || url.includes('denials');
  }
}
